/** P-08 — paper-Rx uploads: upload (camera/file) + list with review status + cancel. */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { getUploadRules } from "../../api/catalogue";
import { cancelUpload, fetchUploadImage, listFamily, listUploads, uploadPrescription } from "../../api/patient";
import { ApiError, describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { Modal } from "../../components/ui/Modal";
import { PdfView } from "../../components/ui/PdfView";
import { Select } from "../../components/ui/Select";
import { RX_UPLOAD_RULE, acceptFor, tooLarge, typeRefused, uploadProblem } from "../../lib/uploadRules";

const STATUS_TONE: Record<string, string> = {
  pending_review: "bg-amber-50 text-amber-700",
  in_review: "bg-blue-50 text-blue-700",
  digitized: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

// The words for each status. A cancelled upload has its own (ORD-07); it showed as a rejection.
const STATUS_LABEL: Record<string, string> = {
  pending_review: "Pending review",
  in_review: "In review",
  digitized: "Digitised",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export function Uploads() {
  const qc = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["uploads"],
    queryFn: ({ pageParam }) => listUploads(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.page + 1 : undefined),
  });
  const { data: family = [] } = useQuery({ queryKey: ["family"], queryFn: listFamily });
  // What an upload accepts, as the server publishes it; its default until that loads.
  const { data: uploadRules } = useQuery({ queryKey: ["upload-rules"], queryFn: getUploadRules, staleTime: Infinity });
  const rule = uploadRules?.prescription ?? RX_UPLOAD_RULE;
  const invalidate = () => qc.invalidateQueries({ queryKey: ["uploads"] });
  
  // console.log("Uploads data:", data);
  const uploads = data?.pages.flatMap((p) => p.data) ?? [];

  // Infinite-scroll sentinel — fetch the next page when it scrolls into view.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [familyId, setFamilyId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const uploadKey = useIdempotencyKey();

  const upload = useMutation({
    mutationFn: (file: File) =>
      uploadPrescription(file, {
        for_family_member_id: familyId || undefined,
        note: note.trim() || undefined,
        idempotencyKey: uploadKey.keyFor({ name: file.name, size: file.size, at: file.lastModified, familyId, note: note.trim() }),
      }),
    onSuccess: () => {
      uploadKey.reset();
      setNote("");
      setError(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      invalidate();
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === "ATTACHMENT_TYPE_NOT_ALLOWED"
          ? typeRefused(rule)
          : e instanceof ApiError && e.code === "ATTACHMENT_TOO_LARGE"
            ? tooLarge(rule)
            : describeError(e),
      ),
  });

  // A failed cancel says why and refreshes the list (ORD-12): review may have started meanwhile.
  const cancel = useMutation({
    mutationFn: cancelUpload,
    onSuccess: invalidate,
    onError: (e) => {
      toast.error(
        e instanceof ApiError && e.code === "UPLOAD_NOT_CANCELLABLE"
          ? "A pharmacist has started reviewing this upload, so it can no longer be cancelled."
          : describeError(e),
      );
      invalidate();
    },
  });

  const resetForm = () => {
    setFile(null);
    setFamilyId("");
    setNote("");
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const isDirty = Boolean(file || familyId || note);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Upload prescription</h2>
        <p className="text-sm text-gray-500">Upload a paper prescription for a pharmacist to digitise.</p>
      </div>

      <div className="space-y-3 rounded-xl border bg-white p-5 shadow-xs">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">For</span>
            <Select
              value={familyId}
              options={[{ value: "", label: "Myself" }, ...family.map((f) => ({ value: f.id, label: f.full_name }))]}
              onChange={setFamilyId}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-lg border px-3 py-1.5" />
          </label>
        </div>
        <input
          ref={fileRef}
          type="file"
          // No `capture` (ORD-19): it forced the camera on phones, so a gallery photo or a PDF
          // couldn't be chosen; without it the phone offers camera, photos and files.
          accept={acceptFor(rule)}
          onChange={(e) => {
            // Checked here, before it is sent (ORD-06/ORD-15), against what the server publishes.
            const picked = e.target.files?.[0] ?? null;
            const problem = picked && uploadProblem(picked, rule);
            setError(problem || null);
            setFile(problem ? null : picked);
            if (problem) e.target.value = "";
          }}
          className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-brand file:px-4 file:py-2 file:text-brand-fg"
        />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={resetForm}
            disabled={!isDirty || upload.isPending}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => file && upload.mutate(file)}
            disabled={!file || upload.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
          >
            {upload.isPending ? "Uploading…" : "Submit"}
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500">Your uploads</h3>
        {uploads.map((u) => (
          <div key={u.id} className="flex flex-col gap-3 rounded-xl border bg-white p-4 shadow-xs sm:flex-row sm:items-center sm:justify-between">
            {/* Stacked on phones, and a long link or word in the note wraps, so the list never scrolls
                sideways and View / Cancel stay on screen (RESP-17). */}
            <div className="min-w-0 wrap-anywhere">
              {/* "Pending review" stays one chip; the date wraps under it when the row is tight (RESP-11). */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[u.status] ?? "bg-gray-100"}`}>
                  {STATUS_LABEL[u.status] ?? u.status.replace(/_/g, " ")}
                </span>
                <span className="text-xs text-gray-400">{new Date(u.created_at).toLocaleString()}</span>
              </div>
              {u.note && <div className="mt-1 text-sm text-gray-600">{u.note}</div>}
              {u.status === "rejected" && u.reject_reason && (
                <div className="mt-1 text-sm text-red-600">Rejected: {u.reject_reason}{u.reject_note ? ` — ${u.reject_note}` : ""}</div>
              )}
              {u.status === "cancelled" && (
                <div className="mt-1 text-sm text-gray-500">You cancelled this upload{u.reject_note ? ` — ${u.reject_note}` : "."}</div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <button onClick={() => setViewingId(u.id)} className="text-sm text-brand hover:underline">
                View
              </button>
              {u.status === "pending_review" && (
                <button
                  onClick={() => {
                    if (!cancel.isPending && window.confirm("Cancel this upload? A pharmacist won't review it.")) cancel.mutate(u.id);
                  }}
                  disabled={cancel.isPending}
                  className="text-sm text-red-600 hover:underline disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ))}
        {!isLoading && uploads.length === 0 && <p className="text-sm text-gray-400">No uploads yet.</p>}

        {/* Sentinel + loading indicator for infinite scroll */}
        <div ref={sentinelRef} />
        {isFetchingNextPage && <p className="text-center text-sm text-gray-400">Loading more…</p>}
        {!hasNextPage && uploads.length > 0 && (
          <p className="text-center text-xs text-gray-500">No more uploads.</p>
        )}
      </div>

      {viewingId && <ImageModal uploadId={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}

/** Modal that fetches and renders an uploaded file inline — <img> for images, PdfView for PDFs. */
function ImageModal({ uploadId, onClose }: { uploadId: string; onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [contentType, setContentType] = useState("");
  const [filename, setFilename] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    fetchUploadImage(uploadId)
      .then((res) => {
        if (cancelled) {
          URL.revokeObjectURL(res.url);
          return;
        }
        url = res.url;
        setSrc(res.url);
        setContentType(res.contentType);
        setFilename(res.filename);
      })
      .catch((e) => !cancelled && setError(describeError(e)));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [uploadId]);

  const isPdf = contentType.includes("pdf");

  // The shared dialog (RESP-18): drawn over the whole screen (portalled, so the page's animated
  // wrapper can't pin it mid-page under the tab bar on a phone), with Escape, a scroll lock and a
  // proper close button.
  return (
    <Modal open title="Uploaded prescription" onClose={onClose} size="lg">
      {error ? (
        <div className="rounded-lg border bg-red-50 p-4 text-sm text-red-600">{error}</div>
      ) : !src ? (
        <div className="flex h-64 items-center justify-center rounded-lg border bg-gray-50 text-sm text-gray-400">
          Loading…
        </div>
      ) : isPdf ? (
        <PdfView url={src} filename={filename || "prescription.pdf"} className="h-[70dvh] w-full rounded-lg border" />
      ) : !contentType.startsWith("image/") ? (
        // Only a real image opens full-size in a new tab; anything else is a download (ORD-02).
        <div className="rounded-lg border bg-gray-50 p-4 text-center text-sm text-gray-500">
          This file can't be previewed.{" "}
          <a href={src} download={filename || "prescription"} className="font-medium text-brand underline">
            Download it
          </a>
        </div>
      ) : (
        <a href={src} target="_blank" rel="noreferrer" className="block">
          <img
            src={src}
            alt="Uploaded prescription"
            className="max-h-[70dvh] w-full rounded-lg border object-contain"
          />
        </a>
      )}
    </Modal>
  );
}
